import {
  FileTextOutlined,
  MoneyCollectOutlined,
  ProductOutlined,
  ScheduleOutlined,
  SettingOutlined,
  ShopOutlined,
  UserOutlined,
  ShareAltOutlined,
  LogoutOutlined,
  ApartmentOutlined,
} from '@ant-design/icons';
import { Avatar, Dropdown, Layout, Menu, Space, Tag } from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import axios from 'axios';
import { first } from 'lodash';
import { MenuProps } from 'rc-menu';
import React, { useLayoutEffect, useState } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from 'react-router-dom';
import { ProductRefundContainer } from './accounting/product-refund/product-refund.container';
import { ProviderEventsContainer } from './accounting/provider-events/provider-events.container';
import { StatementsContainer } from './accounting/statements/statements.container';
import './App.css';
import { DriversContainer } from './drivers/drivers.container';
import { DriversSalaryContainer } from './drivers/salary/drivers-salary.container';
import { LocationsContainer } from './locations/locations.container';
import { LocationsTagsContainer } from './locations/tags/locations-tags.container';
import { LoginContainer } from './login/login.container';
import { OrdersContainer } from './orders/orders.container';
import { OrderStatisticsContainer } from './orders/statistics/order-statistics.container';
import { ProductsContainer } from './products/products.container';
import { ProvidersContainer } from './providers/providers.container';
import { ProvidersTagsContainer } from './providers/tags/providers-tags.container';
import { ScheduleDriversContainer } from './schedules/drivers/schedule-drivers.container';
import { ScheduleOverviewContainer } from './schedules/overview/schedule-overview.container';
import { ScheduleProviderOverviewContainer } from './schedules/providerOverview/schedule-provider-overview.container';
import { PermissionsContainer } from './settings/permissions/permissions.container';
import { RolesContainer } from './settings/roles/roles-container';
import { UsersContainer } from './settings/users/users.container';
import { PrivateRoute } from './shared/private-route';
import { PublicRoute } from './shared/public-route.container';
import { useAuthStore } from './utils/stores/useAuthStore';
import { ReceivablesContainer } from './accounting/receivables/receivables.container';
import ProviderComponent from './providers/provider/provider.component';
import ProviderInfoComponent from './providers/provider/provider-info.component';
import ProviderBusinessInfoComponent from './providers/provider/provider-business-info.component';
import ProviderPaymentInfoComponent from './providers/provider/provider-payment-info.component';
import { ProviderTypesContainer } from './providers/types/provider-types-container';
import ProviderReceivableInfo from './providers/provider/provider-receivable-info.component';
import InstallmentsContainer from './accounting/installments/installments.container';
import { RecommendationsContainer } from './recommendations/recommendations.container';
import { RecommendationStatisticsContainer } from './recommendations/statistics/recommendation-statistics.container';
import DepartmentsContainer from './human-resource/departments/departments.container';
import DepartmentContainer from './human-resource/departments/department/department.container';
import EmployeesContainer from './human-resource/employees/employees.container';
import DepartmentEmployeesContainer from './human-resource/departments/department/department-employees.container';
import PTSchedulesContainer from './schedules/pt-schedule/pt-schedules.container';
import { PTAssignmentContainer } from './schedules/pt-schedule/pt-assignment.container';
import EmployeeSalaryAdjustmentsContainer from './human-resource/employee-salary-adjustments/employee-salary-adjustments.container';
import { ScheduleMenusContainer } from './schedules/menus/schedule-menus.container';
import { EnterpriseOrdersContainer } from './enterprise-orders/enterprise-orders.container';
import { EmployeePayrolls } from './human-resource/employee-payroll/payrolls';

const { Sider } = Layout;

type MenuItem = Required<MenuProps>['items'][number];

function getItem(
  label: React.ReactNode,
  key: React.Key,
  icon?: React.ReactNode,
  children?: MenuItem[],
  type?: 'group'
): MenuItem {
  return {
    key,
    icon,
    children,
    label: children ? (
      label
    ) : (
      <Link to={key.toString()} key={key}>
        {label}
      </Link>
    ),
    type,
  } as MenuItem;
}

const items: MenuItem[] = [
  getItem('供應商管理', '/providers/', <UserOutlined />, [
    getItem('供應商列表', '/providers'),
    getItem('供應商標籤', '/providers/tags'),
    getItem('供應商類型', '/providers/types'),
  ]),
  getItem('商品管理', '/products', <ProductOutlined />),
  getItem('訂單管理', '/orders/', <FileTextOutlined />, [
    getItem('訂單列表', '/orders'),
    getItem('訂單統計', '/orders/statistics'),
  ]),
  getItem('企業訂單', '/enterprise-orders/', <FileTextOutlined />),
  getItem('帳務管理', '/accounting', <MoneyCollectOutlined />, [
    getItem('對帳單管理', '/accounting/statements'),
    getItem('商品退款管理', '/accounting/product-refund'),
    getItem('供應商事件管理', '/accounting/provider-events'),
    getItem('收款管理', '/accounting/receivables'),
    getItem('分期收款管理', '/accounting/installments'),
  ]),
  getItem('大樓管理', '/locations/', <ShopOutlined />, [
    getItem('大樓列表', '/locations'),
    getItem('大樓標籤', '/locations/tags'),
  ]),
  getItem('班表管理', '/schedules', <ScheduleOutlined />, [
    getItem('排菜單', '/schedules/menus'),
    getItem('司機配置', '/schedules/drivers'),
    getItem('班表總覽', '/schedules/overview'),
    getItem('供應商配餐總覽', '/schedules/providerOverview'),
  ]),
  getItem('PT班表', '/schedules/pt', <ScheduleOutlined />),
  getItem('司機管理', '/drivers/', <ProductOutlined />, [
    getItem('司機列表', '/drivers'),
    getItem('司機薪資', '/drivers/salary'),
  ]),
  getItem('推薦用戶管理', '/recommendations', <ShareAltOutlined />, [
    getItem('推薦用戶排名', '/recommendations/ranking'),
    getItem('推薦用戶統計', '/recommendations/statistics'),
  ]),
  getItem('人資管理', '/human-resource', <ApartmentOutlined />, [
    getItem('部門管理', '/human-resource/departments'),
    getItem('員工管理', '/human-resource/employees'),
    getItem('員工薪資調整項目', '/human-resource/employee-salary-adjustments'),
    getItem('員工薪資明細', '/human-resource/employee-payrolls'),
  ]),
  getItem('系統設定', '/settings', <SettingOutlined />, [
    getItem('用戶管理', '/settings/users'),
    getItem('角色管理', '/settings/roles'),
    getItem('權限列表', '/settings/permissions'),
  ]),
];

const App: React.FC = () => {
  const location = useLocation();
  const auth = useAuthStore();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [collapsed, setCollapsed] = useState(true);

  useLayoutEffect(() => {
    if (token && !auth.token) {
      auth.login(token);
    }
  }, [token, auth.login, auth, auth.token]);

  useLayoutEffect(() => {
    if (auth.token) {
      axios.interceptors.request.clear();
      axios.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${auth.token}`;

        return config;
      });
    }
  }, [auth.token]);

  useLayoutEffect(() => {
    axios.interceptors.response.clear();

    axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          auth.logout();
        }

        throw error;
      }
    );
  }, [auth, auth.logout]);

  return (
    <Layout className="min-h-[100vh] max-h-[100vh] h-[100vh]">
      {auth.token && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={(value) => setCollapsed(value)}
        >
          <Menu
            theme="dark"
            mode="inline"
            items={items}
            selectedKeys={[location.pathname]}
          />
        </Sider>
      )}
      <Layout>
        {auth.token && (
          <Header className="flex items-center justify-between px-4">
            <span className="text-2xl font-bold text-gray-200">
              主控台
              {process.env.REACT_APP_ENV && (
                <Tag color="warning" className="ml-1">
                  {process.env.REACT_APP_ENV}
                </Tag>
              )}
            </span>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'logout',
                    icon: <LogoutOutlined />,
                    label: '登出',
                    onClick: auth.logout,
                  },
                ],
              }}
            >
              <Space>
                <Avatar
                  size="large"
                  src={auth.user?.photoUrl}
                  className="bg-gray-200 text-[#001529]"
                >
                  {first(auth.user?.displayName?.split(''))}
                </Avatar>
                <span className="text-gray-200">{auth.user?.displayName}</span>
              </Space>
            </Dropdown>
          </Header>
        )}
        <Content className="relative p-4 overflow-auto">
          <Routes>
            <Route element={<PrivateRoute />}>
              <Route path="orders">
                <Route index key="index" element={<OrdersContainer />} />
                <Route
                  key="statistics"
                  path="statistics"
                  element={<OrderStatisticsContainer />}
                />
                <Route
                  path="*"
                  key="default"
                  element={<Navigate to="" replace />}
                />
              </Route>
              <Route
                path="enterprise-orders"
                element={<EnterpriseOrdersContainer />}
              />
              <Route
                path="locations"
                children={[
                  <Route index key="index" element={<LocationsContainer />} />,
                  <Route
                    key="tags"
                    path="tags"
                    element={<LocationsTagsContainer />}
                  />,
                  <Route
                    path="*"
                    key="default"
                    element={<Navigate to="" replace />}
                  />,
                ]}
              />
              <Route path="products" element={<ProductsContainer />} />
              <Route path="accounting">
                <Route
                  index
                  key="index"
                  element={<Navigate to="statements" replace />}
                />
                <Route
                  path="statements"
                  key="statements"
                  element={<StatementsContainer />}
                />
                <Route
                  path="product-refund"
                  key="product-refund"
                  element={<ProductRefundContainer />}
                />
                <Route
                  path="provider-events"
                  key="provider-events"
                  element={<ProviderEventsContainer />}
                />
                <Route
                  path="receivables"
                  key="receivables"
                  element={<ReceivablesContainer />}
                />
                <Route
                  path="installments"
                  key={'installments'}
                  element={<InstallmentsContainer />}
                />
                <Route
                  path="*"
                  key="default"
                  element={<Navigate to="" replace />}
                />
              </Route>
              <Route path="providers">
                <Route index key="index" element={<ProvidersContainer />} />
                <Route
                  key="tags"
                  path="tags"
                  element={<ProvidersTagsContainer />}
                />
                <Route path=":id" element={<ProviderComponent />}>
                  <Route index element={<Navigate to="info" replace />} />
                  <Route path="info" element={<ProviderInfoComponent />} />
                  <Route
                    path="business-info"
                    element={<ProviderBusinessInfoComponent />}
                  />
                  <Route
                    path="payment-info"
                    element={<ProviderPaymentInfoComponent />}
                  />
                  <Route
                    path="receivable-info"
                    element={<ProviderReceivableInfo />}
                  />
                  <Route path="*" element={<Navigate to="" replace />} />
                </Route>
                <Route path="types" element={<ProviderTypesContainer />} />

                <Route
                  path="*"
                  key="default"
                  element={<Navigate to="" replace />}
                />
              </Route>
              <Route path="schedules">
                <Route
                  index
                  key="index"
                  element={<Navigate to="menus" replace />}
                />

                <Route
                  key="menus"
                  path="menus"
                  element={<ScheduleMenusContainer />}
                />

                <Route
                  key="drivers"
                  path="drivers"
                  element={<ScheduleDriversContainer />}
                />

                <Route
                  key="overview"
                  path="overview"
                  element={<ScheduleOverviewContainer />}
                />

                <Route
                  key="providerOverview"
                  path="providerOverview"
                  element={<ScheduleProviderOverviewContainer />}
                />

                <Route path="pt">
                  <Route index element={<PTSchedulesContainer />} />
                  <Route
                    key="pt-assign"
                    path=":scheduleId/assign"
                    element={<PTAssignmentContainer />}
                  />
                  <Route path="*" element={<Navigate to="" replace />} />
                </Route>

                <Route
                  path="*"
                  key="default"
                  element={<Navigate to="" replace />}
                />
              </Route>
              <Route
                path="drivers"
                children={[
                  <Route index key="index" element={<DriversContainer />} />,
                  <Route
                    key="salary"
                    path="salary"
                    element={<DriversSalaryContainer />}
                  />,
                  <Route
                    path="*"
                    key="default"
                    element={<Navigate to="" replace />}
                  />,
                ]}
              />
              <Route path="human-resource">
                <Route index element={<Navigate to="departments" replace />} />
                <Route path="departments">
                  <Route index element={<DepartmentsContainer />} />
                  <Route path=":id" element={<DepartmentContainer />}>
                    <Route
                      index
                      element={<Navigate to="employees" replace />}
                    />
                    <Route
                      path="employees"
                      element={<DepartmentEmployeesContainer />}
                    />
                    <Route path="*" element={<Navigate to="" replace />} />
                  </Route>
                  <Route path="*" element={<Navigate to="" replace />} />
                </Route>
                <Route path="employees" element={<EmployeesContainer />} />
                <Route
                  path="employee-salary-adjustments"
                  element={<EmployeeSalaryAdjustmentsContainer />}
                />
                <Route
                  path="employee-payrolls"
                  element={<EmployeePayrolls />}
                />
                <Route path="*" element={<Navigate to="" replace />} />
              </Route>
              <Route
                path="settings"
                children={[
                  <Route
                    index
                    key="index"
                    element={<Navigate to="users" replace />}
                  />,
                  <Route
                    key="users"
                    path="users"
                    element={<UsersContainer />}
                  />,
                  <Route
                    key="roles"
                    path="roles"
                    element={<RolesContainer />}
                  />,
                  <Route
                    key="permissions"
                    path="permissions"
                    element={<PermissionsContainer />}
                  />,
                  <Route
                    path="*"
                    key="default"
                    element={<Navigate to="" replace />}
                  />,
                ]}
              />
              <Route
                path="recommendations"
                children={[
                  <Route
                    index
                    key="index"
                    element={<Navigate to="statistics" replace />}
                  />,
                  <Route
                    key="ranking"
                    path="ranking"
                    element={<RecommendationsContainer />}
                  />,
                  <Route
                    key="statistics"
                    path="statistics"
                    element={<RecommendationStatisticsContainer />}
                  />,
                  <Route
                    path="*"
                    key="default"
                    element={<Navigate to="" replace />}
                  />,
                ]}
              />
              <Route path="*" element={<Navigate to="providers" replace />} />
            </Route>
            <Route element={<PublicRoute />}>
              <Route path="login" element={<LoginContainer />} />
            </Route>
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
